import React, { useState } from 'react';

interface StarRatingControlProps {
  messageId: string;
  currentGrade?: 'A' | 'B' | 'C' | 'D' | 'F';
  onRate: (grade: 'A' | 'B' | 'C' | 'D' | 'F') => void;
  onDelete?: () => void; // NEW: Called when double-clicking to delete grade
  disabled?: boolean;
}

/**
 * Star Rating Control Component
 * 
 * Displays 5 monochrome stars that convert to letter grades:
 * 5 stars = A
 * 4 stars = B
 * 3 stars = C
 * 2 stars = D
 * 1 star = F
 */
export const StarRatingControl: React.FC<StarRatingControlProps> = ({
  messageId,
  currentGrade,
  onRate,
  onDelete,
  disabled = false
}) => {
  const [hoverStars, setHoverStars] = useState<number | null>(null);
  
  // Convert grade to star count
  const gradeToStars = (grade?: 'A' | 'B' | 'C' | 'D' | 'F'): number => {
    if (!grade) return 0;
    const mapping: Record<string, number> = {
      'A': 5,
      'B': 4,
      'C': 3,
      'D': 2,
      'F': 1
    };
    return mapping[grade] || 0;
  };
  
  // Convert star count to grade
  const starsToGrade = (stars: number): 'A' | 'B' | 'C' | 'D' | 'F' => {
    const mapping: Record<number, 'A' | 'B' | 'C' | 'D' | 'F'> = {
      5: 'A',
      4: 'B',
      3: 'C',
      2: 'D',
      1: 'F'
    };
    return mapping[stars] || 'F';
  };
  
  const currentStars = gradeToStars(currentGrade);
  const displayStars = hoverStars !== null ? hoverStars : currentStars;
  
  const handleClick = (stars: number) => {
    console.log('🌟 StarRatingControl clicked:', { stars, disabled, messageId });
    if (disabled) return;
    const grade = starsToGrade(stars);
    console.log('🌟 Calling onRate with grade:', grade);
    onRate(grade);
  };
  
  const handleDoubleClick = () => {
    console.log('🌟 StarRatingControl double-clicked:', { messageId, disabled });
    if (disabled || !onDelete) return;
    console.log('🌟 Calling onDelete to remove grade');
    onDelete();
  };
  
  // Get tooltip text for each star
  const getTooltip = (stars: number): string => {
    const tooltips: Record<number, string> = {
      5: 'A: super intelligent',
      4: 'B: generally intelligent',
      3: 'C: artificially intelligent',
      2: 'D: unintelligent',
      1: 'F: failed'
    };
    return tooltips[stars] || '';
  };
  
  return (
    <div 
      className="inline-flex items-center gap-0.5"
      onMouseLeave={() => setHoverStars(null)}
    >
      {[1, 2, 3, 4, 5].map((starNum) => {
        const isFilled = starNum <= displayStars;
        const isHovering = hoverStars !== null && starNum <= hoverStars;
        
        return (
          <button
            key={starNum}
            onClick={() => handleClick(starNum)}
            onDoubleClick={handleDoubleClick}
            onMouseEnter={() => !disabled && setHoverStars(starNum)}
            disabled={disabled}
            className={`
              p-0.5 transition-all duration-150
              ${disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:scale-110'}
            `}
            title={onDelete ? `${getTooltip(starNum)} (double-click to remove rating)` : getTooltip(starNum)}
            aria-label={`Rate ${starsToGrade(starNum)}`}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill={isFilled ? 'currentColor' : 'none'}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`
                transition-colors duration-150
                ${isFilled ? 'text-foreground' : 'text-foreground/50'}
                ${isHovering ? 'text-foreground/80' : ''}
              `}
            >
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </button>
        );
      })}
    </div>
  );
};

